import React, { useState, useEffect } from "react";
import { Navbar, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  setShowErrorModal,
  onChangeSettingTabKey,
} from "../store/slices/preferenceSlice";

import ConnectTripletex from "./ConnectTripletex";
import SyncSettings from "./SyncSettings";
import Subscription from "./Subscription";
import Confirmation from "./Confirmation";
import { Loading } from "../components/Loading";

import { getUrl } from "../helpers/getUrl";
// cms
import { useCmsHook } from "../helpers/cmsHook";
import CmsEditAndSave from "../helpers/CmsEditAndSave";
import { CmsConstructor } from "../helpers/CmsConstructor";

const WINDOW = Object.freeze({
  OAUTH: "Connect Window",
  SYNC_SETTINGS: "Sync Settings",
  SUBSCRIPTION: "Subscription",
  CONFIRMATION: "Confirmation",
});

const windowObject = {
  [WINDOW.OAUTH]: <ConnectTripletex />,
  [WINDOW.SYNC_SETTINGS]: <SyncSettings />,
  [WINDOW.SUBSCRIPTION]: <Subscription />,
  [WINDOW.CONFIRMATION]: <Confirmation />,
};

function Setup() {
  const { setCmsInstance, cmsInstance, isEdit } = useCmsHook();

  const [currentWindow, setCurrentWindow] = useState("");
  const [loading, setLoading] = useState(false);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    isTripletexConnected,
    settingsActiveTab,
    status: { loading: setupLoading },
  } = useSelector((state) => state.preference);

  const {
    subscription: { planData },
  } = useSelector((state) => state.subscription);

  const { isContactSyncRulesSaved } = useSelector((state) => state.contact);

  const { isInvoiceSyncRulesSaved } = useSelector((state) => state.invoice);

  const { isProductSyncRulesSaved } = useSelector((state) => state.product);

  const {
    isMappingSaved,
    mapping: {
      defaultMappings: { order: orderMappings },
    },
  } = useSelector((state) => state.mapping);

  const onBackNavigation = () => {
    const portalId = searchParams.get("portalId");
    const userId = searchParams.get("userId");

    if (currentWindow === WINDOW.CONFIRMATION) {
      navigate(
        `${window.location.pathname}?portalId=${portalId}&userId=${userId}&syncSettings=true`
      );
      if (planData.plan === "basic") {
        dispatch(
          onChangeSettingTabKey({
            selectedTab: "contact",
          })
        );
      } else {
        dispatch(
          onChangeSettingTabKey({
            selectedTab: "order",
          })
        );
      }
      setCurrentWindow(WINDOW.SYNC_SETTINGS);
    }

    if (
      currentWindow === WINDOW.SYNC_SETTINGS &&
      settingsActiveTab === "order"
    ) {
      dispatch(
        onChangeSettingTabKey({
          selectedTab: "product",
          setupLoading,
          isInvoiceSyncRulesSaved,
          isInvoiceMappingsSaved: isMappingSaved.order,
          orderMappings,
        })
      );
    }

    if (
      currentWindow === WINDOW.SYNC_SETTINGS &&
      settingsActiveTab === "product"
    ) {
      dispatch(
        onChangeSettingTabKey({
          selectedTab: "contact",
          setupLoading,
          isProductSyncRulesSaved,
          isProductFieldMappingsSaved: isMappingSaved.productFieldMappings,
        })
      );
    }

    if (
      currentWindow === WINDOW.SYNC_SETTINGS &&
      settingsActiveTab === "contact"
    ) {
      navigate(
        `${window.location.pathname}?portalId=${portalId}&userId=${userId}&paymentStatus=none`
      );
      setCurrentWindow(WINDOW.SUBSCRIPTION);
    }

    if (currentWindow === WINDOW.SUBSCRIPTION) {
      navigate(
        `${window.location.pathname}?portalId=${portalId}&userId=${userId}`
      );
      setCurrentWindow(WINDOW.OAUTH);
    }
  };

  const onNextNavigation = () => {
    const portalId = searchParams.get("portalId");
    const userId = searchParams.get("userId");

    if (currentWindow === WINDOW.OAUTH) {
      navigate(
        `${window.location.pathname}?portalId=${portalId}&userId=${userId}&paymentStatus=none`
      );
      setCurrentWindow(WINDOW.SUBSCRIPTION);
    }

    if (currentWindow === WINDOW.SUBSCRIPTION) {
      navigate(
        `${window.location.pathname}?portalId=${portalId}&userId=${userId}&syncSettings=true`
      );
      dispatch(onChangeSettingTabKey({ selectedTab: "contact" }));
      setCurrentWindow(WINDOW.SYNC_SETTINGS);
    }

    if (
      currentWindow === WINDOW.SYNC_SETTINGS &&
      settingsActiveTab === "contact"
    ) {
      if (planData.plan === "basic") {
        navigate(
          `${window.location.pathname}?portalId=${portalId}&userId=${userId}&confirmation=true`
        );
        setCurrentWindow(WINDOW.CONFIRMATION);
      } else {
        dispatch(
          onChangeSettingTabKey({
            selectedTab: "product",
            setupLoading,
            isContactSyncRulesSaved,
            isContactMappingsSaved: isMappingSaved.customer,
          })
        );
      }
    }

    if (
      currentWindow === WINDOW.SYNC_SETTINGS &&
      settingsActiveTab === "product"
    ) {
      dispatch(
        onChangeSettingTabKey({
          selectedTab: "order",
          setupLoading,
          isProductSyncRulesSaved,
          isProductFieldMappingsSaved: isMappingSaved.productFieldMappings,
        })
      );
    }

    if (
      currentWindow === WINDOW.SYNC_SETTINGS &&
      settingsActiveTab === "order"
    ) {
      navigate(
        `${window.location.pathname}?portalId=${portalId}&userId=${userId}&confirmation=true`
      );
      setCurrentWindow(WINDOW.CONFIRMATION);
    }
  };

  const install = async () => {
    try {
      const code = searchParams.get("code");
      const state = searchParams.get("state");
      const portalId = searchParams.get("portalId");
      const paymentStatus = searchParams.get("paymentStatus");
      const confirmation = searchParams.get("confirmation");
      const syncSettings = searchParams.get("syncSettings");
      const userId = searchParams.get("userId");

      if (code) {
        setLoading(true);
        const { data: res } = await axios.post(getUrl("REACT_APP_INSTALL"), {
          code,
          state,
        });

        if (res.tripletex) {
          setCurrentWindow(WINDOW.SUBSCRIPTION);
          navigate(
            `../setup?portalId=${res.portalId}&userId=${res.userId}&paymentStatus=none`
          );
        } else {
          setCurrentWindow(WINDOW.OAUTH);
          navigate(`../setup?portalId=${res.portalId}&userId=${res.userId}`);
        }
      } else if (portalId && userId && paymentStatus) {
        setCurrentWindow(WINDOW.SUBSCRIPTION);
      } else if (portalId && userId && confirmation) {
        setCurrentWindow(WINDOW.CONFIRMATION);
      } else if (portalId && userId && syncSettings) {
        setCurrentWindow(WINDOW.SYNC_SETTINGS);
      } else if (portalId && userId) {
        setCurrentWindow(WINDOW.OAUTH);
      }
    } catch (error) {
      console.log("Error", error);
      let errorMessage;
      if (error.response && error.response.data.message)
        errorMessage = error.response.data.message;
      else errorMessage = error.message;
      dispatch(
        setShowErrorModal({
          message: errorMessage,
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const getBackToHubspot = () => {
    const portalId = searchParams.get("portalId");
    window.open(
      `https://app.hubspot.com/contacts/${portalId}/companies`,
      "_self"
    );
  };

  useEffect(() => {
    install();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const cmsInstance = async () => {
      const cmsObj = await CmsConstructor(dispatch);
      setCmsInstance(cmsObj);
    };
    cmsInstance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      {loading ? (
        <Loading />
      ) : (
        <div>
          <Navbar fixed="top" className="px-4 py-3 navbar">
            <div className="d-flex justify-content-between w-100">
              {currentWindow === WINDOW.OAUTH ? (
                <p className="hide-button"></p>
              ) : (
                <Button
                  className="navbar-back-button my-auto"
                  onClick={onBackNavigation}
                >
                  Back
                </Button>
              )}

              <div className="text-center">
                {cmsInstance && cmsInstance.showRichText("cmsContent.header")}
              </div>

              {currentWindow === WINDOW.CONFIRMATION ? (
                <Button
                  className="navbar-next-button my-auto"
                  onClick={getBackToHubspot}
                >
                  Finish
                </Button>
              ) : (
                <>
                  {(currentWindow === WINDOW.OAUTH && !isTripletexConnected) ||
                  (currentWindow === WINDOW.SYNC_SETTINGS &&
                    settingsActiveTab === "contact" &&
                    (!isMappingSaved.customer || !isContactSyncRulesSaved)) ||
                  (settingsActiveTab === "order" &&
                    (!isMappingSaved.order || !isInvoiceSyncRulesSaved)) ||
                  (settingsActiveTab === "product" &&
                    (!isProductSyncRulesSaved ||
                      !isMappingSaved.productFieldMappings)) ? (
                    <OverlayTrigger
                      placement="left"
                      overlay={
                        <Tooltip style={{ fontSize: "11px" }}>
                          {showTooltipText(
                            currentWindow,
                            isMappingSaved.customer,
                            isMappingSaved.order,
                            isTripletexConnected,
                            isContactSyncRulesSaved,
                            isInvoiceSyncRulesSaved,
                            isProductSyncRulesSaved,
                            isMappingSaved.productFieldMappings,
                            orderMappings
                          )}
                        </Tooltip>
                      }
                    >
                      <Button
                        style={{ opacity: "0.7", cursor: "not-allowed" }}
                        className="navbar-next-button me-3"
                      >
                        Next
                      </Button>
                    </OverlayTrigger>
                  ) : (
                    <Button
                      className="navbar-next-button my-auto"
                      onClick={onNextNavigation}
                    >
                      Next
                    </Button>
                  )}
                </>
              )}
            </div>
          </Navbar>

          <div
            style={{
              marginTop: `${
                isEdit && window.location.pathname === "/setup"
                  ? "200px"
                  : "100px"
              }`,
            }}
          >
            <div className="d-flex flex-column align-items-center">
              <CmsEditAndSave />
            </div>
            <div>{windowObject[currentWindow]}</div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Setup;

const showTooltipText = (
  currentWindow,
  isCustomerMappingsSaved,
  isOrderMappingsSaved,
  isTripletexConnected,
  isContactSyncRulesSaved,
  isInvoiceSyncRulesSaved,
  isProductSyncRulesSaved,
  isProductFieldMappingsSaved,
  orderMappings
) => {
  if (currentWindow === WINDOW.SYNC_SETTINGS) {
    if (!isCustomerMappingsSaved) {
      return "Please save the contact mappings before proceeding further";
    }
    if (!isOrderMappingsSaved) {
      return "Please save the order mappings before proceeding further";
    }
    if (!isContactSyncRulesSaved) {
      return "Please save the contact sync rules, before proceeding further";
    }
    // if (!isContactDefaultMappingsSaved) {
    //   return "Please save the contact default mappings, before proceeding further";
    // }
    if (!isInvoiceSyncRulesSaved) {
      return "Please save the invoice sync rules before proceeding further";
    }
    // if (!isInvoiceDefaultMappingsSaved) {
    //   return "Please save the invoice default mappings before proceeding further";
    // }
    if (!isProductSyncRulesSaved) {
      return "Please save the product sync rules before proceeding further";
    }
    if (!isProductFieldMappingsSaved) {
      return "Please save the product field mappings before proceeding further";
    }
  } else if (currentWindow === WINDOW.OAUTH && !isTripletexConnected) {
    return "Please Connect Tripletex before proceeding further";
  }
};
